<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50"
    >
      <div class="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <template v-if="!success">
          <div class="mb-4 flex justify-center">
            <svg
              width="48"
              height="48"
              viewBox="0 0 48 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="48" height="48" rx="12" fill="#F0F0F0" />
              <path
                d="M20 16a5 5 0 1 1 0 10 5 5 0 0 1 0-10Zm0 12c-5 0-9 2-9 5v1h13"
                stroke="#333"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                fill="none"
              />
              <path
                d="M30 28v-2a3 3 0 0 1 6 0v2"
                stroke="#333"
                stroke-width="2"
                stroke-linecap="round"
                fill="none"
              />
              <rect
                x="28"
                y="28"
                width="10"
                height="7"
                rx="1.5"
                stroke="#333"
                stroke-width="2"
                fill="none"
              />
              <circle cx="33" cy="31.5" r="1" fill="#333" />
            </svg>
          </div>
          <h2 class="mb-2 text-center text-lg font-semibold text-gray-900">
            Sign in faster next time
          </h2>
          <p class="mb-6 text-center text-sm text-gray-600">
            Create a passkey to sign in with your fingerprint, face, or screen
            lock instead of a password.
          </p>
          <div class="flex flex-col gap-3">
            <button
              type="button"
              class="w-full rounded-md bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
              :disabled="isRegistering"
              @click="handleSetup"
            >
              {{ isRegistering ? 'Setting up...' : 'Set up passkey' }}
            </button>
            <button
              type="button"
              class="w-full rounded-md px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none"
              @click="handleNotNow"
            >
              Not now
            </button>
            <button
              type="button"
              class="w-full px-4 py-1 text-xs text-gray-400 hover:text-gray-600 focus:outline-none"
              @click="handleDontAskAgain"
            >
              Don't ask again
            </button>
          </div>
          <p
            v-if="errorMessage"
            class="mt-3 text-center text-sm text-red-600"
          >
            {{ errorMessage }}
          </p>
        </template>
        <template v-else>
          <div class="flex flex-col items-center py-4">
            <svg
              class="mb-4 h-12 w-12 text-green-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h2 class="mb-2 text-lg font-semibold text-gray-900">
              Passkey created
            </h2>
            <p class="mb-4 text-center text-sm text-gray-600">
              Next time you sign in, you can use your fingerprint, face, or
              screen lock.
            </p>
            <button
              type="button"
              class="rounded-md bg-blue-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none"
              @click="visible = false"
            >
              Done
            </button>
          </div>
        </template>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { isWebAuthnSupported } from '@/composables/useWebAuthn';
import { usePasskeys } from '@/composables/usePasskeys';

const visible = ref(false);
const success = ref(false);
const isRegistering = ref(false);
const errorMessage = ref(null);

const { fetchPasskeys, passkeys, registerPasskey } = usePasskeys();

onMounted(async () => {
  if (!sessionStorage.getItem('gpx_just_logged_in')) return;
  sessionStorage.removeItem('gpx_just_logged_in');

  if (sessionStorage.getItem('gpx_passkey_prompt_seen')) return;
  if (localStorage.getItem('gpx_passkey_prompt_dismissed')) return;

  if (!isWebAuthnSupported()) return;

  try {
    await fetchPasskeys();
    if (passkeys.value.length > 0) return;
  } catch {
    return;
  }

  visible.value = true;
});

async function handleSetup() {
  errorMessage.value = null;
  isRegistering.value = true;
  try {
    await registerPasskey('My passkey');
    success.value = true;
  } catch (err) {
    if (err?.name === 'NotAllowedError') {
      errorMessage.value = 'Passkey setup was cancelled.';
    } else {
      errorMessage.value =
        err?.message || 'Failed to create passkey. Please try again.';
    }
  } finally {
    isRegistering.value = false;
  }
}

function handleNotNow() {
  sessionStorage.setItem('gpx_passkey_prompt_seen', '1');
  visible.value = false;
}

function handleDontAskAgain() {
  localStorage.setItem('gpx_passkey_prompt_dismissed', '1');
  visible.value = false;
}
</script>
